/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// @mui material components
import Icon from '@mui/material/Icon';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';
import Grid from '@mui/material/Grid';
import TableContainer from '@mui/material/TableContainer';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import FormQuantity from 'components/Form/FormQuantity';
import FormPrice from 'components/Form/FormPrice';
import MDInput from 'components/MDInput';
import MDSelectAuto from 'components/MDSelectAuto';

// Material Dashboard 2 PRO React examples
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

import SearchProductCost from './SearchProductCost';
import LinkProduct from '../quotation/LinkProduct';
import ImportExcel from './ImportExcel';
import FormMarkup from './FormMarkup';

const initAttr = {
  id: '',
  quotation_branch_id: '',
  barcode_id: '',
  quantity: '',
  unit_id: '',
  code: '',
  name: '',
  name_en: '',
  price: '',
  image_id: '',
  enable: 'Y',
  created_at: '',
  updated_at: '',
};

const initParent = {
  id: '',
  code: '',
  remark: '',
  type_id: '',
  supplier_id: '',
  discount_type: 'B',
  discount_value: '',
  discount_percent: '',
  shipping: '',
  discount_unit: 'Y',
  approve: 'N',
  vat_id: '2',
  status: 'edit',
  deposit: '',
  print_url: '',
  created_at: '',
  updated_at: '',
};

const initPhoto = {
  id: '',
  code: '',
  sku: '',
  name: '',
  image: '',
};

const QuotationBranchList = forwardRef(({ setLimitApprove }, ref) => {
  const mod = 'quotation_branch_list';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [attr, setAttr] = useState(initAttr);
  const [record, setRecord] = useState([]);
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(true);
  const [parent, setParent] = useState(initParent);
  const [discountChange, setDiscountChange] = useState('');
  const [percentChange, setPercentChange] = useState('');
  const [shippingChange, setShippingChange] = useState('');
  const [depositChange, setDepositChange] = useState('');
  const [modalPhoto, setModalPhoto] = useState(false);
  const [photo, setPhoto] = useState(initPhoto);
  const [modalAdd, setModalAdd] = useState(false);
  const [modalEdit, setModalEdit] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [cboUnit, setCboUnit] = useState([]);
  const [unit, setUnit] = useState({ id: '', name: '' });

  const showNotify = (msg, color) => {
    setNotify(
      <Snackbar
        open
        autoHideDuration={3000}
        onClose={() => setNotify(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotify(null)} severity={color} variant='filled'>
          {msg}
        </Alert>
      </Snackbar>
    );
  };

  const view = () => {
    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      ref_id: id,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);
        setParent(result.parent);
        setDiscountChange(result.parent.discount_value);
        setPercentChange(result.parent.discount_percent);
        setShippingChange(result.parent.shipping);
        setLimitApprove(result.parent.approve);
        setLoading(false);
      }
    });
  };

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setCboUnit(result.unit);

        view();
      } else {
        Func.exitApp();
      }
    });
  };

  const open = (item) => {
    setLoading(true);
    setConfirm(false);
    setAttr(initAttr);

    FetchData('GET', `/${mod}/${item.id}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setAttr(result.data);
        setUnit({ id: result.data.unit_id, name: result.data.unit_name });

        setModalEdit(true);

        setLoading(false);
      }
    });
  };

  const addConfirm = () => {
    if (!Func.verifyLength(attr.name, 1)) return;

    setConfirm(true);
  };

  const add = () => {
    setLoading(true);
    setConfirm(false);
    setModalAdd(false);

    FetchData('PUT', `/${mod}`, app.token, {
      quotation_branch_id: id,
      barcode_id: 0,
      quantity: attr.quantity,
      unit_id: attr.unit_id,
      code: attr.code,
      name: attr.name,
      name_en: attr.name_en,
      price: attr.price,
      image_id: 0,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view();
        showNotify(t(`alert.addcomplete`), 'success');
      } else {
        setModalAdd(true);
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const addItem = (item) => {
    setLoading(true);

    FetchData('PUT', `/${mod}`, app.token, {
      quotation_branch_id: id,
      barcode_id: item.id,
      quantity: item.quantity,
      unit_id: item.unit_id,
      code: item.code,
      name: item.product_name,
      name_en: item.name_en,
      price: item.price,
      image_id: item.image_id,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view();
        showNotify(t(`alert.addcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const importData = (item) => {
    setLoading(true);
    setRecord([]);

    FetchData('OPTIONS', `/${mod}/import`, app.token, {
      quotation_branch_id: id,
      list: item,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view();
        showNotify(t(`alert.addcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const editConfirm = () => {
    if (!Func.verifyLength(attr.name, 1)) return;

    setConfirm(true);
  };

  const editName = () => {
    setLoading(true);
    setConfirm(false);
    setModalEdit(false);

    FetchData('PATCH', `/${mod}/${attr.id}`, app.token, {
      kind: 'NAME',
      code: attr.code,
      name: attr.name,
      name_en: attr.name_en,
      unit_id: attr.unit_id,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view();
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        setModalEdit(true);
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const edit = (item, kind) => {
    FetchData('PATCH', `/${mod}/${item.id}`, app.token, {
      kind: kind,
      quantity: item.quantity,
      markup: item.markup,
    }).then((result) => {
      if (result.status === 'ok') {
        view();
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const editDiscount = (item) => {
    FetchData('OPTIONS', `/${mod}/discount`, app.token, {
      ref_id: id,
      discount_value: item.discount_value,
      discount_type: 'B',
    }).then((result) => {
      if (result.status === 'ok') {
        setParent(result.parent);
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const editDiscountPercent = (item) => {
    FetchData('OPTIONS', `/${mod}/discount`, app.token, {
      ref_id: id,
      discount_percent: item.discount_percent,
      discount_type: 'P',
    }).then((result) => {
      if (result.status === 'ok') {
        setParent(result.parent);
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const sendValue = () => {
    if (parent.discount_value * 1 !== discountChange * 1) {
      setDiscountChange(parent.discount_value);
      editDiscount({ discount_value: parent.discount_value, discount_type: parent.discount_type });
    }
  };

  const sendPercent = () => {
    if (parent.discount_percent * 1 !== percentChange * 1) {
      setPercentChange(parent.discount_percent);
      editDiscountPercent({
        discount_percent: parent.discount_percent,
        discount_type: parent.discount_type,
      });
    }
  };

  const sendShipping = () => {
    if (parent.shipping * 1 === shippingChange * 1) return;

    setShippingChange(parent.shipping);

    FetchData('OPTIONS', `/${mod}/shipping`, app.token, {
      ref_id: id,
      shipping: parent.shipping,
    }).then((result) => {
      if (result.status === 'ok') {
        setParent(result.parent);
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const sendDeposit = () => {
    if (parent.deposit * 1 === depositChange * 1) return;

    setDepositChange(parent.deposit);

    FetchData('OPTIONS', `/${mod}/deposit`, app.token, {
      ref_id: id,
      deposit: parent.deposit,
    }).then((result) => {
      if (result.status === 'ok') {
        setParent(result.parent);
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const del = (item) => {
    setLoading(true);

    FetchData('DELETE', `/${mod}/${item.id}`, app.token, {}).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view();
        showNotify(t(`alert.deletecomplete`), 'success');
      } else {
        showNotify(t(`alert.deleteerror`), 'error');
      }
    });
  };

  const linkProduct = (item, list_id) => {
    setLoading(true);

    FetchData('PATCH', `/${mod}/${list_id}`, app.token, {
      kind: 'NAME',
      barcode_id: item.id,
      image_id: item.image_id,
      code: item.code,
      name: item.name,
      name_en: item.name_en,
      unit_id: item.unit_id,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view();
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const btnOpen = (item) => {
    if (parent.status === 'reject' || parent.status === 'cancel') return item.code;

    return (
      <MDButton
        variant='text'
        color='info'
        size='small'
        sx={{ fontSize: '1rem', whiteSpace: 'nowrap', p: 0, textAlign: 'left', display: 'block' }}
        onClick={() => open(item)}
      >
        {item.code}
      </MDButton>
    );
  };

  const btnDel = (item) => {
    if (parent.status === 'reject' || parent.status === 'cancel') return null;

    return (
      <MDButton
        variant='text'
        tabIndex={-1}
        color='error'
        size='small'
        iconOnly
        onClick={() => del(item)}
      >
        <Icon>delete</Icon>
      </MDButton>
    );
  };

  const btnNew = (item) => {
    if (parent.status === 'reject' || parent.status === 'cancel') return null;
    if (item.barcode_id * 1 > 0) return null;

    return <LinkProduct parent={item} callback={linkProduct} />;
  };

  const renderHeaderEdit = () => {
    return (
      <TableHead>
        <TableRow>
          <TableCell align='center'>No.</TableCell>
          <TableCell align='center'>รูป</TableCell>
          <TableCell>รหัส</TableCell>
          <TableCell>New</TableCell>
          <TableCell>สินค้า</TableCell>
          <TableCell align='center' width={120} className='nowrap'>
            จำนวน
          </TableCell>
          <TableCell align='left' width={120} className='nowrap'>
            หน่วย
          </TableCell>
          <TableCell align='right' width={120} className='nowrap'>
            ราคา
          </TableCell>
          <TableCell align='center' width={120} className='nowrap'>
            Markup
          </TableCell>
          <TableCell align='right' className='nowrap'>
            ราคารวม
          </TableCell>
          <TableCell width={10}>&nbsp;</TableCell>
        </TableRow>
      </TableHead>
    );
  };

  const renderRecordEdit = () => {
    return (
      <TableBody>
        {record.map((row) => (
          <TableRow key={row.id}>
            <TableCell align='center'>{row.no}</TableCell>
            <TableCell
              onClick={() => {
                setPhoto(row);
                setModalPhoto(true);
              }}
            >
              {Func.imageDisplay(row)}
            </TableCell>
            <TableCell>{btnOpen(row)}</TableCell>
            <TableCell>{btnNew(row)}</TableCell>
            <TableCell>
              <MDBox width={200}>{row.name}</MDBox>
            </TableCell>
            <TableCell align='right'>
              <FormQuantity parent={row} edit={edit} />
            </TableCell>
            <TableCell align='left'>{row.unit_name}</TableCell>
            <TableCell align='right'>{Func.numberDisplay(row.price, 2)}</TableCell>
            <TableCell align='right'>
              <FormMarkup parent={row} edit={edit} />
            </TableCell>
            <TableCell align='right'>{Func.numberDisplay(row.total, 2)}</TableCell>
            <TableCell align='center'>{btnDel(row)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    );
  };

  const renderHeader = () => {
    return (
      <TableHead>
        <TableRow>
          <TableCell align='center'>No.</TableCell>
          <TableCell align='center'>รูป</TableCell>
          <TableCell>รหัส</TableCell>
          <TableCell>New</TableCell>
          <TableCell>สินค้า</TableCell>
          <TableCell align='center' width={120} className='nowrap'>
            จำนวน
          </TableCell>
          <TableCell align='left' width={120} className='nowrap'>
            หน่วย
          </TableCell>
          <TableCell align='right' width={120} className='nowrap'>
            ราคา
          </TableCell>
          <TableCell align='center' width={120} className='nowrap'>
            Markup
          </TableCell>
          <TableCell align='right' className='nowrap'>
            ราคารวม
          </TableCell>
        </TableRow>
      </TableHead>
    );
  };

  const renderRecord = () => {
    return (
      <TableBody>
        {loading ? (
          <TableRow>
            <TableCell colSpan={11} align='center' className='color-default'>
              กำลังโหลดข้อมูล ...
            </TableCell>
          </TableRow>
        ) : null}
        {record.map((row) => (
          <TableRow key={row.id}>
            <TableCell align='center'>{row.no}</TableCell>
            <TableCell
              onClick={() => {
                setPhoto(row);
                setModalPhoto(true);
              }}
            >
              {Func.imageDisplay(row)}
            </TableCell>
            <TableCell>{btnOpen(row)}</TableCell>
            <TableCell>{btnNew(row)}</TableCell>
            <TableCell>
              <MDBox width={200}>{row.name}</MDBox>
            </TableCell>
            <TableCell align='right'>{Func.numberDisplay(row.quantity, 0)}</TableCell>
            <TableCell align='left'>{row.unit_name}</TableCell>
            <TableCell align='right'>{Func.numberDisplay(row.price, 2)}</TableCell>
            <TableCell align='right'>{Func.numberDisplay(row.markup, 2)} %</TableCell>
            <TableCell align='right'>{Func.numberDisplay(row.total, 2)}</TableCell>
            <TableCell align='center'>{btnDel(row)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    );
  };

  const txtDiscount = () => {
    return (
      <MDBox
        noValidate
        component='form'
        role='form'
        onSubmit={(e) => {
          e.preventDefault();

          sendValue();
        }}
      >
        <MDInput
          onChange={(e) => setParent({ ...parent, discount_value: e.target.value })}
          onFocus={(e) => e.target.select()}
          onBlur={() => sendValue()}
          value={parent.discount_value}
          variant='standard'
          type='number'
          InputProps={{
            inputProps: {
              maxLength: 20,
              autoComplete: 'off',
              className: 'text-right',
              sx: { minWidth: 80, fontWeight: 'bold', fontSize: '1.3rem' },
            },
          }}
          fullWidth
        />
      </MDBox>
    );
  };

  const txtDiscountPercent = () => {
    return (
      <MDBox
        noValidate
        component='form'
        role='form'
        onSubmit={(e) => {
          e.preventDefault();

          sendPercent();
        }}
      >
        <MDInput
          onChange={(e) => setParent({ ...parent, discount_percent: e.target.value })}
          onFocus={(e) => e.target.select()}
          onBlur={() => sendPercent()}
          value={parent.discount_percent}
          variant='standard'
          type='number'
          InputProps={{
            inputProps: {
              maxLength: 20,
              autoComplete: 'off',
              className: 'text-right',
              sx: { minWidth: 80, fontWeight: 'bold', fontSize: '1.3rem' },
            },
          }}
          fullWidth
        />
      </MDBox>
    );
  };

  const txtShipping = () => {
    return (
      <MDBox
        noValidate
        component='form'
        role='form'
        onSubmit={(e) => {
          e.preventDefault();

          sendShipping();
        }}
      >
        <MDInput
          onChange={(e) => setParent({ ...parent, shipping: e.target.value })}
          onFocus={(e) => {
            e.target.select();
          }}
          onBlur={() => {
            sendShipping();
          }}
          value={parent.shipping}
          variant='standard'
          type='number'
          InputProps={{
            inputProps: {
              maxLength: 20,
              autoComplete: 'off',
              className: 'text-right',
              sx: { minWidth: 80, fontWeight: 'bold', fontSize: '1.3rem' },
            },
          }}
          fullWidth
        />
      </MDBox>
    );
  };

  const txtDeposit = () => {
    return (
      <MDBox
        noValidate
        component='form'
        role='form'
        onSubmit={(e) => {
          e.preventDefault();

          sendDeposit();
        }}
      >
        <MDInput
          onChange={(e) => setParent({ ...parent, deposit: e.target.value })}
          onFocus={(e) => {
            e.target.select();
          }}
          onBlur={() => {
            sendDeposit();
          }}
          value={parent.deposit}
          variant='standard'
          type='number'
          InputProps={{
            inputProps: {
              maxLength: 20,
              autoComplete: 'off',
              className: 'text-right',
              sx: { minWidth: 80, fontWeight: 'bold', fontSize: '1.3rem' },
            },
          }}
          fullWidth
        />
      </MDBox>
    );
  };

  const renderSummary = () => {
    return (
      <Table aria-label='summary table'>
        <TableBody>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>มูลค่าสินค้า</MDTypography>
            </TableCell>
            <TableCell align='right' width='200'>
              <MDTypography variant='h5'>{Func.numberDisplay(parent.total, 2)}</MDTypography>
            </TableCell>
            <TableCell align='right' width={50}>
              <MDTypography variant='h5'>บาท</MDTypography>
            </TableCell>
          </TableRow>
          {/* <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>หักส่วนลด</MDTypography>
            </TableCell>
            <TableCell align='right'>
              <MDTypography variant='h5'>
                {Func.numberDisplay(parent.discount_value, 2)}
              </MDTypography>
            </TableCell>
            <TableCell align='right'>
              <MDTypography variant='h5'>บาท</MDTypography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>หักส่วนลด</MDTypography>
            </TableCell>
            <TableCell align='right'>
              <MDTypography variant='h5'>
                {Func.numberDisplay(parent.discount_percent, 2)}
              </MDTypography>
            </TableCell>
            <TableCell align='right'>
              <MDTypography variant='h5'>%</MDTypography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>มูลค่าสินค้าหลังหักส่วนลด</MDTypography>
            </TableCell>
            <TableCell align='right'>
              <MDTypography variant='h5'>{Func.numberDisplay(parent.amount, 2)}</MDTypography>
            </TableCell>
            <TableCell align='right' width={50}>
              <MDTypography variant='h5'>บาท</MDTypography>
            </TableCell>
          </TableRow> */}
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>
                ภาษีมูลค่าเพิ่ม {Func.numberDisplay(parent.vat_value, 0)}%
              </MDTypography>
            </TableCell>
            <TableCell align='right'>
              <MDTypography variant='h5'>{Func.numberDisplay(parent.vat, 2)}</MDTypography>
            </TableCell>
            <TableCell align='right' width={50}>
              <MDTypography variant='h5'>บาท</MDTypography>
            </TableCell>
          </TableRow>
          {/* <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>มูลค่ารวมภาษี</MDTypography>
            </TableCell>
            <TableCell align='right' width='200'>
              <MDTypography variant='h5' color='primary'>
                {Func.numberDisplay(parent.amount_vat, 2)}
              </MDTypography>
            </TableCell>
            <TableCell align='right'>
              <MDTypography variant='h5'>บาท</MDTypography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>ค่าจัดส่ง</MDTypography>
            </TableCell>
            <TableCell align='right' width='200'>
              <MDTypography variant='h5'>{Func.numberDisplay(parent.shipping, 2)}</MDTypography>
            </TableCell>
            <TableCell align='right'>
              <MDTypography variant='h5'>บาท</MDTypography>
            </TableCell>
          </TableRow> */}
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>รวมทั้งสิ้น</MDTypography>
            </TableCell>
            <TableCell align='right' width='200'>
              <MDTypography variant='h5' color='info'>
                {Func.numberDisplay(parent.nettotal, 2)}
              </MDTypography>
            </TableCell>
            <TableCell align='right' width={50}>
              <MDTypography variant='h5'>บาท</MDTypography>
            </TableCell>
          </TableRow>
          {/* <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>มัดจำ</MDTypography>
            </TableCell>
            <TableCell align='right' width='200'>
              <MDTypography variant='h5'>{Func.numberDisplay(parent.deposit, 2)}</MDTypography>
            </TableCell>
            <TableCell align='right'>
              <MDTypography variant='h5'>บาท</MDTypography>
            </TableCell>
          </TableRow> */}
        </TableBody>
      </Table>
    );
  };

  const renderButton = () => {
    return (
      <MDBox display='flex' justifyContent='end' gap={1} p={2}>
        {parent.status === 'reject' || parent.status === 'cancel' ? null : (
          // <SearchProductCost callback={addItem} />
          <ImportExcel callback={importData} />
        )}
      </MDBox>
    );
  };

  const renderList = () => {
    return (
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <MDBox pt={3} pl={2}>
              <MDTypography variant='h5'>{t(`element.order`)}</MDTypography>
            </MDBox>
          </Grid>
          <Grid item xs={6}>
            {renderButton()}
          </Grid>
          <Grid item xs={12}>
            <TableContainer sx={{ boxShadow: 0, minHeight: 300 }}>
              <Table aria-label='simple table'>
                {parent.status === 'reject' || parent.status === 'cancel'
                  ? renderHeader()
                  : renderHeaderEdit()}
                {parent.status === 'reject' || parent.status === 'cancel'
                  ? renderRecord()
                  : renderRecordEdit()}
              </Table>
            </TableContainer>
            <TableContainer sx={{ boxShadow: 0, mt: 2 }}>{renderSummary()}</TableContainer>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderPhoto = () => {
    return (
      <Dialog
        open={modalPhoto}
        maxWidth='sm'
        keepMounted
        aria-describedby='dialog-image'
        fullWidth
        onClose={() => setModalPhoto(false)}
      >
        <DialogTitle>
          {photo.code} <span className='color-default'>{photo.sku}</span>
          <IconButton
            aria-label='close'
            onClick={() => setModalPhoto(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox mb={1}>
            <MDTypography variant='body'>{photo.name}</MDTypography>
          </MDBox>
          <MDBox
            component='img'
            src={photo.image}
            alt=''
            borderRadius='lg'
            width='100%'
            position='relative'
            sx={{ boxShadow: 0 }}
          />
        </DialogContent>
      </Dialog>
    );
  };

  const renderInfo = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <MDInput
            label='รหัสสินค้า'
            onChange={(e) => setAttr({ ...attr, code: e.target.value })}
            value={attr.code}
            variant='standard'
            fullWidth
            inputProps={{
              autoComplete: 'off',
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label='ชื่อสินค้า (TH)'
            onChange={(e) => setAttr({ ...attr, name: e.target.value })}
            value={attr.name}
            variant='standard'
            fullWidth
            inputProps={{
              autoComplete: 'off',
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label='ชื่อสินค้า (EN)'
            onChange={(e) => setAttr({ ...attr, name_en: e.target.value })}
            value={attr.name_en}
            variant='standard'
            fullWidth
            inputProps={{
              autoComplete: 'off',
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label='ราคา'
            onChange={(e) => setAttr({ ...attr, price: e.target.value })}
            onFocus={(e) => e.target.select()}
            value={attr.price}
            variant='standard'
            type='number'
            fullWidth
            InputProps={{
              autoComplete: 'off',
              endAdornment: (
                <InputAdornment position='start'>
                  <span>บาท</span>
                </InputAdornment>
              ),
              inputProps: {
                autoComplete: 'off',
                className: 'text-right',
              },
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <MDInput
            label='จำนวน'
            onChange={(e) => setAttr({ ...attr, quantity: e.target.value })}
            onFocus={(e) => e.target.select()}
            value={attr.quantity}
            variant='standard'
            type='number'
            fullWidth
            InputProps={{
              autoComplete: 'off',
              inputProps: {
                autoComplete: 'off',
                className: 'text-right',
              },
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <MDSelectAuto
            label='หน่วย'
            data={cboUnit}
            value={unit || null}
            onChange={(e, v) => {
              e.preventDefault();
              setAttr({ ...attr, unit_id: v.id });
              return setUnit(v);
            }}
          />
        </Grid>
      </Grid>
    );
  };

  const renderAdd = () => {
    return (
      <Dialog open={modalAdd} maxWidth='sm' keepMounted aria-describedby='dialog-add' fullWidth>
        <DialogTitle>
          {t(`element.add`)}
          <IconButton
            aria-label='close'
            onClick={() => setModalAdd(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox p={3}>{renderInfo()}</MDBox>
        </DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant='text' color='error' onClick={() => setConfirm(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display='flex' justifyContent='flex-end'>
                <MDButton variant='outlined' color='success' onClick={add}>
                  <Icon>save</Icon>&nbsp;{t(`element.saveconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant='text' color='info' onClick={addConfirm}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderEdit = () => {
    return (
      <Dialog open={modalEdit} maxWidth='sm' keepMounted aria-describedby='dialog-edit' fullWidth>
        <DialogTitle>
          {t(`element.edit`)} : {attr.code}
          <IconButton
            aria-label='close'
            onClick={() => setModalEdit(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox p={3}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <MDInput
                  label='รหัสสินค้า'
                  onChange={(e) => setAttr({ ...attr, code: e.target.value })}
                  value={attr.code}
                  variant='standard'
                  fullWidth
                  inputProps={{
                    autoComplete: 'off',
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <MDInput
                  label='ชื่อสินค้า (TH)'
                  onChange={(e) => setAttr({ ...attr, name: e.target.value })}
                  value={attr.name}
                  variant='standard'
                  fullWidth
                  inputProps={{
                    autoComplete: 'off',
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <MDInput
                  label='ชื่อสินค้า (EN)'
                  onChange={(e) => setAttr({ ...attr, name_en: e.target.value })}
                  value={attr.name_en}
                  variant='standard'
                  fullWidth
                  inputProps={{
                    autoComplete: 'off',
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <MDSelectAuto
                  label='หน่วย'
                  data={cboUnit}
                  value={unit || null}
                  onChange={(e, v) => {
                    e.preventDefault();
                    setAttr({ ...attr, unit_id: v.id });
                    return setUnit(v);
                  }}
                />
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant='text' color='error' onClick={() => setConfirm(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display='flex' justifyContent='flex-end'>
                <MDButton variant='outlined' color='success' onClick={editName}>
                  <Icon>save</Icon>&nbsp;{t(`element.saveconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant='text' color='success' onClick={editConfirm}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  useImperativeHandle(ref, () => {
    return {
      view,
    };
  });

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <div>
      {renderList()}
      {renderAdd()}
      {renderEdit()}
      {renderLoading()}
      {renderPhoto()}
      {notify}
    </div>
  );
});

QuotationBranchList.propTypes = {
  setLimitApprove: PropTypes.func.isRequired,
};

export default QuotationBranchList;
